/**
 * Login Page — password step, then a 2FA step (verify a code, or first-time enrollment).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, TrendingUp, Lock, User, ShieldCheck, Loader2 } from 'lucide-react';
import TwoFactorSetup from '../components/TwoFactorSetup';

const Shell = ({ subtitle, children }) => (
  <div className="min-h-screen flex items-center justify-center p-6">
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-bg-blue mb-4">
          <TrendingUp className="w-8 h-8 text-white" />
        </div>
        <h1 className="font-display text-3xl font-bold gradient-text-blue">Paeru Exchange</h1>
        <p className="text-text-secondary mt-2">{subtitle}</p>
        <p className="text-text-muted text-xs mt-1">פארו • Aramaic for "Money"</p>
      </div>
      <div className="card p-8">{children}</div>
    </div>
  </div>
);

const Login = () => {
  const [step, setStep] = useState('password'); // password | verify | enroll
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const { login, verifyMfaLogin, applySession, error, clearError } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    const res = await login(username, password);
    setLoading(false);
    if (res.status === 'mfa') { setMfaToken(res.mfaToken); setStep('verify'); }
    else if (res.status === 'enroll') { setMfaToken(res.mfaToken); setStep('enroll'); }
    // 'ok' → the app redirects automatically; 'error' → shown via context error.
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length < 6) return;
    setLoading(true);
    setCodeError('');
    const res = await verifyMfaLogin(mfaToken, code);
    setLoading(false);
    if (!res.success) { setCodeError(res.error || 'Invalid code'); setCode(''); }
    // success → app redirects.
  };

  if (step === 'verify') {
    return (
      <Shell subtitle="Two-factor authentication">
        <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-5 h-5 text-accent-blue" /><h2 className="font-display text-2xl font-semibold">Verify it's you</h2></div>
        <p className="text-text-secondary text-sm mb-6">Enter the 6-digit code from your authenticator app.</p>
        {codeError && <div className="mb-4 p-3 rounded-lg bg-accent-red/10 border border-accent-red/20"><p className="text-accent-red text-sm">{codeError}</p></div>}
        <form onSubmit={handleVerify}>
          <input
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setCodeError(''); }}
            placeholder="000000"
            inputMode="numeric"
            autoFocus
            className="input text-center tracking-[0.5em] text-2xl font-mono mb-2"
          />
          <p className="text-xs text-text-muted mb-4">Lost your device? Enter one of your backup codes instead.</p>
          <button type="submit" disabled={code.length < 6 || loading} className="w-full btn-primary py-4 text-lg disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} Verify
          </button>
        </form>
        <button onClick={() => { setStep('password'); setCode(''); setMfaToken(''); }} className="mt-4 text-sm text-text-muted hover:text-text-secondary w-full text-center">← Back to sign in</button>
      </Shell>
    );
  }

  if (step === 'enroll') {
    return (
      <Shell subtitle="Secure your account">
        <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-5 h-5 text-accent-purple" /><h2 className="font-display text-2xl font-semibold">Set up two-factor</h2></div>
        <p className="text-text-secondary text-sm mb-6">Two-factor authentication is required. It only takes a minute.</p>
        <TwoFactorSetup authToken={mfaToken} onComplete={(session) => { if (session) applySession(session); }} />
        <button onClick={() => { setStep('password'); setMfaToken(''); }} className="mt-4 text-sm text-text-muted hover:text-text-secondary w-full text-center">← Back to sign in</button>
      </Shell>
    );
  }

  return (
    <Shell subtitle="AI-Powered Trading Intelligence">
      <h2 className="font-display text-2xl font-semibold mb-6">Welcome back</h2>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-accent-red/10 border border-accent-red/20">
          <p className="text-accent-red text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-5">
          <label className="block text-text-secondary text-sm mb-2">Username</label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
            <input
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); clearError(); }}
              placeholder="Enter your username"
              className="input input-with-icon"
              autoComplete="username"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-text-secondary text-sm mb-2">Password</label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); clearError(); }}
              placeholder="Enter your password"
              className="input input-with-icon input-with-right-icon"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !username || !password}
          className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p className="mt-6 text-center text-text-secondary">
        Don't have an account?{' '}
        <Link to="/register" className="text-accent-blue hover:underline">
          Create one
        </Link>
      </p>
    </Shell>
  );
};

export default Login;
